import React from 'react';
import PropTypes from 'prop-types';

import {Box, Typography, Collapse} from 'views/components';
import {Translate, translate, CardSelector} from 'views/module';
import {Home, Description, Handshake, MoreHoriz, Info} from 'views/icons';

const WorkEligibilitySection = (props) => {
  const {onWorkEligibilityChange, workEligibility = {}, setValidity} = props;

  // Work eligibility options with icons
  const workEligibilityOptions = [
    {
      id: 'citizenPermanent',
      label: translate('caregiverScreening.WorkEligibility.Options.CitizenPermanent.Label'),
      icon: Home,
      description: translate('caregiverScreening.WorkEligibility.Options.CitizenPermanent.Description'),
    },
    {
      id: 'temporaryResident',
      label: translate('caregiverScreening.WorkEligibility.Options.TemporaryResident.Label'),
      icon: Description,
      description: translate('caregiverScreening.WorkEligibility.Options.TemporaryResident.Description'),
    },
    {
      id: 'seekingSponsorship',
      label: translate('caregiverScreening.WorkEligibility.Options.SeekingSponsorship.Label'),
      icon: Handshake,
      description: translate('caregiverScreening.WorkEligibility.Options.SeekingSponsorship.Description'),
    },
    {
      id: 'other',
      label: translate('caregiverScreening.WorkEligibility.Options.Other.Label'),
      icon: MoreHoriz,
      description: translate('caregiverScreening.WorkEligibility.Options.Other.Description'),
    },
  ];

  // Get current selection
  const selectedEligibility = workEligibility.eligibilityStatus || null;

  // Helper function to render the appropriate icon
  const renderIcon = (eligibilityId, iconProps = {}) => {
    const defaultProps = {fontSize: 20, color: 'text.primary', ...iconProps};

    switch (eligibilityId) {
      case 'citizenPermanent':
        return <Home sx={defaultProps} />;
      case 'temporaryResident':
        return <Description sx={defaultProps} />;
      case 'seekingSponsorship':
        return <Handshake sx={defaultProps} />;
      case 'other':
        return <MoreHoriz sx={defaultProps} />;
      default:
        return null;
    }
  };

  // Update validity when selection changes
  React.useEffect(() => {
    if (setValidity) {
      setValidity(selectedEligibility !== null);
    }
  }, [selectedEligibility, setValidity]);

  // Handle eligibility selection
  const handleEligibilityChange = (eligibilityId) => {
    const updatedEligibility = {
      ...workEligibility,
      eligibilityStatus: eligibilityId,
    };

    if (onWorkEligibilityChange) {
      onWorkEligibilityChange(updatedEligibility);
    }
  };

  return (
    <Box sx={{p: 3}}>
      <Typography variant="h5" gutterBottom align="center" fontWeight={600}>
        <Translate expression='caregiverScreening.WorkEligibility.Title'/>
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{mb: 3}}>
        <Translate expression='caregiverScreening.WorkEligibility.Subtitle'/>
      </Typography>

      {/* Work Eligibility Selection */}
      <CardSelector
        title={translate('caregiverScreening.WorkEligibility.WorkAuthorizationTitle')}
        description={translate('caregiverScreening.WorkEligibility.WorkAuthorizationDescription')}
        options={workEligibilityOptions}
        selectedValue={selectedEligibility}
        onSelectionChange={handleEligibilityChange}
        gridProps={{size: {xs: 6, sm: 6}}}
        cardHeight={180}
      />

      {/* Important Info Box */}
      <Box
        sx={{
          mt: 4,
          p: 2,
          backgroundColor: 'primary.light',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'primary.main',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
        }}
      >
        <Info sx={{color: 'primary.main', mt: 0.2, fontSize: 20}} />
        <Box>
          <Typography variant="subtitle2" color="primary.dark" fontWeight={600} sx={{mb: 0.5}}>
            <Translate expression='caregiverScreening.WorkEligibility.ImportantInfo.Title'/>
          </Typography>
          <Typography variant="body2" color="primary.dark">
            <Translate expression='caregiverScreening.WorkEligibility.ImportantInfo.Message'/>
          </Typography>
        </Box>
      </Box>

      {/* Summary */}
      <Collapse in={Boolean(selectedEligibility)} timeout="auto" unmountOnExit>
        <Box
          sx={{
            mt: 3,
            p: 3,
            backgroundColor: 'action.hover',
            borderRadius: 1,
          }}
        >
          <Typography variant="h6" fontWeight={600} sx={{mb: 2}}>
            <Translate expression='caregiverScreening.WorkEligibility.Summary.Title'/>
          </Typography>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
              {renderIcon(selectedEligibility, {fontSize: 20, color: 'text.primary'})}
              <Typography variant="body1" color="text.primary">
                {workEligibilityOptions.find((option) => option.id === selectedEligibility)?.label}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              |
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {workEligibilityOptions.find((option) => option.id === selectedEligibility)?.description}
            </Typography>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

WorkEligibilitySection.propTypes = {
  onWorkEligibilityChange: PropTypes.func,
  workEligibility: PropTypes.shape({
    eligibilityStatus: PropTypes.string,
  }),
  setValidity: PropTypes.func,
};

export default React.memo(WorkEligibilitySection);
